import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import BopBackLink from '../../../../components/BopBackLink';
import ApiInspector from '../../../../components/ApiInspector';
import ComboFiltro from '../../../../components/ComboFiltro';
import { apiFetch } from '../../../../lib/api';
import { showToast } from '../../../../components/Toast';
import { formatCurrency, formatQuantity, formatNumberInput, parseItalianNumber } from '../../../../utils/formatters';
import { useFiltroSocieta } from '../../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../../context/PermessiContext';
import { useDirtyForm } from '../../../../hooks/useDirtyForm';
import { API_DOCS } from './WizardOrdiniFornitori.api';
import './WizardOrdiniFornitori.css';

// ── Tipi ──────────────────────────────────────────────────────────────────────

interface FornitoreRef {
  id: number;
  ragioneSociale: string;
}

interface SorgenteRda {
  rigaRdaId: number;
  rdaId: number;
  rdaEtichetta: string;
  ordine: { id: number; numero: string } | null;
  commessa: { id: number; numero: string; descrizione: string | null } | null;
  quantita: number;
  ordinata: number;
  residuo: number;
  note: string | null;
}

interface ArticoloDaOrdinare {
  chiave: string;
  articoloId: number | null;
  codice: string | null;
  descrizione: string;
  tipologia: string | null;
  unitaMisura: string | null;
  prezzoStandard: number | null;
  fornitoreAbituale: FornitoreRef | null;
  totaleDaOrdinare: number;
  sorgenti: SorgenteRda[];
}

interface SedeOption {
  id: number;
  nome: string;
}

interface NominativoMin {
  id: number;
  ragioneSociale: string | null;
  nome: string | null;
  cognome: string | null;
}

// Riga di un ordine in composizione (step 2)
interface RigaProposta {
  rigaRdaId: number;
  chiave: string;
  articoloId: number | null;
  codice: string | null;
  descrizione: string;
  unitaMisura: string | null;
  quantita: number;
  prezzo: string; // stringa in formato italiano, editabile
  rdaEtichetta: string;
}

// Ordine in composizione (step 2)
interface OrdineProposto {
  uid: number;
  fornitoreId: number | null;
  sedeId: string;
  note: string;
  righe: RigaProposta[];
}

interface RisultatoOrdine {
  esito: 'CREATO' | 'ERRORE';
  ordineFornitoreId?: number;
  fornitoreRagioneSociale?: string;
  nRighe?: number;
  motivo?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

// Estrae il messaggio dagli errori API ({error: string | {code,message}})
function estraiErrore(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const payload = (err as { payload?: { error?: string | { code?: string; message?: string } } }).payload;
    const e = payload?.error;
    if (e && typeof e === 'object' && e.message) return e.message;
    if (typeof e === 'string' && e) return e;
    if (err instanceof Error && err.message && err.message !== '[object Object]') return err.message;
  }
  return fallback;
}

function renderEsito(esito: RisultatoOrdine['esito']): React.ReactNode {
  if (esito === 'CREATO') return <span className="bop-badge bop-badge-success">Creato</span>;
  return <span className="bop-badge bop-badge-danger">Errore</span>;
}

// Quantità come stringa italiana per gli input (es. 3.5 → "3,5")
function qtaToInput(n: number): string {
  return String(n).replace('.', ',');
}

// Nome visualizzabile di un nominativo fornitore
function nomeNominativo(n: NominativoMin): string {
  return n.ragioneSociale || [n.cognome, n.nome].filter(Boolean).join(' ') || `Fornitore ${n.id}`;
}

// Totale stimato di una card: Σ quantità × prezzo atteso.
// completo=false se almeno una riga non ha un prezzo valido → si mostra "—".
function totaleCard(o: OrdineProposto): { totale: number; completo: boolean } {
  let totale = 0;
  let completo = true;
  for (const r of o.righe) {
    const p = parseItalianNumber(r.prezzo);
    if (!Number.isFinite(p)) { completo = false; continue; }
    totale += r.quantita * p;
  }
  return { totale: Math.round(totale * 100) / 100, completo };
}

// ── Pagina ────────────────────────────────────────────────────────────────────

export default function WizardOrdiniFornitori() {
  const navigate = useNavigate();
  const { societaId, listaSocieta, isDisabled, pronta } = useFiltroSocieta();
  const { isSuperadmin } = usePermesso();

  // ?commessaId=X: wizard aperto dal cruscotto commessa, solo le righe RDA di quella commessa
  const [searchParams, setSearchParams] = useSearchParams();
  const commessaIdFiltro = searchParams.get('commessaId');

  // Stato locale società (solo SUPERADMIN con "Tutte" può modificarlo)
  // Dal cruscotto arriva anche ?societaId=: preseleziona la società quando in topbar c'è "Tutte"
  const [societaIdLocale, setSocietaIdLocale] = useState(() => searchParams.get('societaId') || '');
  const societaIdEff = societaId || societaIdLocale;

  // Step del wizard
  const [step, setStep] = useState<1 | 2>(1);

  // Ricerca testo client-side (step 1)
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');

  // Dati step 1
  const [articoli, setArticoli] = useState<ArticoloDaOrdinare[]>([]);
  const [loading, setLoading] = useState(false);

  // Selezione sorgenti per articolo (chiave articolo → set di rigaRdaId)
  const [sel, setSel] = useState<Map<string, Set<number>>>(new Map());
  // Quantità da ordinare per sorgente (rigaRdaId → stringa con virgola)
  const [qta, setQta] = useState<Map<number, string>>(new Map());
  // Articoli con expander sorgenti aperto
  const [espansi, setEspansi] = useState<Set<string>>(new Set());
  // Sorgenti con quantità non valida (evidenziate dopo il tentativo di avanzare)
  const [erroriQta, setErroriQta] = useState<Set<number>>(new Set());

  // Contesto step 2: sedi e fornitori della società
  const [sedi, setSedi] = useState<SedeOption[]>([]);
  const [fornitori, setFornitori] = useState<NominativoMin[]>([]);

  // Composizione (step 2) + firma della selezione con cui è stata generata
  const [ordini, setOrdini] = useState<OrdineProposto[]>([]);
  const [firmaComposizione, setFirmaComposizione] = useState('');
  const uidRef = useRef(1);
  const generaUid = () => uidRef.current++;

  const [creando, setCreando] = useState(false);

  // Esiti (null = wizard attivo, valorizzato = pannello risultati)
  const [risultati, setRisultati] = useState<RisultatoOrdine[] | null>(null);
  const [creatiCount, setCreatiCount] = useState(0);

  // Debounce ricerca testo
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim().toLowerCase()), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  // ── Caricamento articoli da ordinare ────────────────────────────────────────

  const caricaArticoli = useCallback(async () => {
    if (!pronta || !societaIdEff) {
      setArticoli([]);
      setSel(new Map());
      setQta(new Map());
      return;
    }
    setLoading(true);
    try {
      // @api GET /api/rda/da-ordinare?societaId&commessaId -> {"success":true,"data":[{"chiave":"string","articoloId":"number|null","codice":"string|null","descrizione":"string","tipologia":"string|null","unitaMisura":"string|null","prezzoStandard":"number|null","fornitoreAbituale":{"id":"number","ragioneSociale":"string"},"totaleDaOrdinare":"number","sorgenti":[{"rigaRdaId":"number","rdaId":"number","rdaEtichetta":"string","ordine":{"id":"number","numero":"string"},"commessa":{"id":"number","numero":"string","descrizione":"string"},"quantita":"number","ordinata":"number","residuo":"number","note":"string|null"}]}]}
      const filtroCommessa = commessaIdFiltro ? `&commessaId=${commessaIdFiltro}` : '';
      const r = await apiFetch<{ success: boolean; data: ArticoloDaOrdinare[] }>(
        `/api/rda/da-ordinare?societaId=${societaIdEff}${filtroCommessa}`
      );
      const data = r.data || [];
      setArticoli(data);
      // Di default tutte le sorgenti selezionate, quantità = residuo
      setSel(new Map(data.map(a => [a.chiave, new Set(a.sorgenti.map(s => s.rigaRdaId))])));
      const qtaInit = new Map<number, string>();
      for (const a of data) {
        for (const s of a.sorgenti) qtaInit.set(s.rigaRdaId, qtaToInput(s.residuo));
      }
      setQta(qtaInit);
      setEspansi(new Set());
      setErroriQta(new Set());
      // Reset composizione: verrà rigenerata al passaggio allo step 2
      setStep(1);
      setOrdini([]);
      setFirmaComposizione('');
    } catch (err: unknown) {
      showToast(estraiErrore(err, 'Errore nel caricamento degli articoli da ordinare'), 'error');
      setArticoli([]);
      setSel(new Map());
      setQta(new Map());
    } finally {
      setLoading(false);
    }
  }, [pronta, societaIdEff, commessaIdFiltro]);

  useEffect(() => { caricaArticoli(); }, [caricaArticoli]);

  // Etichetta della commessa filtrata (dalle sorgenti: tutte della stessa commessa)
  const commessaFiltrata = commessaIdFiltro
    ? articoli.flatMap(a => a.sorgenti).find(s => s.commessa)?.commessa ?? null
    : null;

  const rimuoviFiltroCommessa = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('commessaId');
    setSearchParams(next);
  };

  // ── Caricamento contesto step 2 (sedi + fornitori della società) ───────────

  const caricaContesto = useCallback(async () => {
    if (!societaIdEff) {
      setSedi([]);
      setFornitori([]);
      return;
    }
    try {
      // @api GET /api/sedi?societaId -> {"sedi":[{"id":"number","nome":"string","societaId":"number"}]}
      const rSedi = await apiFetch<{ sedi: SedeOption[] }>(`/api/sedi?societaId=${societaIdEff}`);
      setSedi(rSedi.sedi || []);
    } catch (err: unknown) {
      showToast(estraiErrore(err, 'Errore nel caricamento delle sedi'), 'error');
      setSedi([]);
    }
    try {
      // @api GET /api/nominativi?ruolo=FORNITORE&limit=500&societaId -> {"data":[{"id":"number","ragioneSociale":"string|null","nome":"string|null","cognome":"string|null"}],"total":"number"}
      const rFornitori = await apiFetch<{ data: NominativoMin[]; total: number }>(
        `/api/nominativi?ruolo=FORNITORE&limit=500&societaId=${societaIdEff}`
      );
      setFornitori(rFornitori.data || []);
    } catch (err: unknown) {
      showToast(estraiErrore(err, 'Errore nel caricamento dei fornitori'), 'error');
      setFornitori([]);
    }
  }, [societaIdEff]);

  useEffect(() => { caricaContesto(); }, [caricaContesto]);

  // Quando le sedi arrivano dopo la composizione: assegna la default alle card senza sede
  useEffect(() => {
    if (sedi.length === 0) return;
    const sedeDefault = String(sedi[0].id);
    setOrdini(prev => prev.some(o => !o.sedeId)
      ? prev.map(o => (o.sedeId ? o : { ...o, sedeId: sedeDefault }))
      : prev);
  }, [sedi]);

  // ── Fornitori: opzioni combo e risoluzione nomi ─────────────────────────────

  const fornitoriItems = useMemo(
    () => fornitori
      .map(f => ({ id: String(f.id), nome: nomeNominativo(f) }))
      .sort((a, b) => a.nome.localeCompare(b.nome, 'it')),
    [fornitori]
  );

  const nomeFornitore = useCallback((id: number | null): string | null => {
    if (id == null) return null;
    const f = fornitori.find(x => x.id === id);
    if (f) return nomeNominativo(f);
    for (const a of articoli) {
      if (a.fornitoreAbituale?.id === id) return a.fornitoreAbituale.ragioneSociale;
    }
    return `Fornitore ${id}`;
  }, [fornitori, articoli]);

  // ── Filtro testo client-side (codice/descrizione/fornitore/RDA/ordine/commessa)

  const articoliFiltrati = useMemo(() => {
    if (!q) return articoli;
    return articoli.filter(a => {
      const testoArticolo = [
        a.codice || '',
        a.descrizione || '',
        a.fornitoreAbituale?.ragioneSociale || '',
      ].join(' ').toLowerCase();
      if (testoArticolo.includes(q)) return true;
      return a.sorgenti.some(s =>
        [
          s.rdaEtichetta,
          s.ordine?.numero || '',
          s.commessa?.numero || '',
          s.commessa?.descrizione || '',
        ].join(' ').toLowerCase().includes(q)
      );
    });
  }, [articoli, q]);

  // ── Selezione (step 1) ──────────────────────────────────────────────────────

  const toggleSorgente = (chiave: string, rigaRdaId: number) => {
    setSel(prev => {
      const next = new Map(prev);
      const s = new Set(next.get(chiave) || []);
      if (s.has(rigaRdaId)) s.delete(rigaRdaId); else s.add(rigaRdaId);
      next.set(chiave, s);
      return next;
    });
  };

  const toggleArticolo = (a: ArticoloDaOrdinare) => {
    setSel(prev => {
      const next = new Map(prev);
      const s = next.get(a.chiave) || new Set<number>();
      if (s.size === a.sorgenti.length) {
        next.set(a.chiave, new Set());
      } else {
        next.set(a.chiave, new Set(a.sorgenti.map(x => x.rigaRdaId)));
      }
      return next;
    });
  };

  const toggleEspanso = (chiave: string) => {
    setEspansi(prev => {
      const next = new Set(prev);
      if (next.has(chiave)) next.delete(chiave); else next.add(chiave);
      return next;
    });
  };

  const setQtaSorgente = (rigaRdaId: number, val: string) => {
    setQta(prev => new Map(prev).set(rigaRdaId, val));
    setErroriQta(prev => {
      if (!prev.has(rigaRdaId)) return prev;
      const next = new Set(prev);
      next.delete(rigaRdaId);
      return next;
    });
  };

  const nRigheSelezionate = useMemo(
    () => Array.from(sel.values()).reduce((tot, s) => tot + s.size, 0),
    [sel]
  );

  // Firma canonica di selezione + quantità: se cambia, la composizione va rigenerata
  const firmaSelezione = useMemo(
    () => articoli
      .map(a => {
        const s = sel.get(a.chiave);
        if (!s || s.size === 0) return null;
        const parti = Array.from(s).sort((x, y) => x - y).map(id => `${id}=${qta.get(id) || ''}`);
        return `${a.chiave}:${parti.join(',')}`;
      })
      .filter(Boolean)
      .join(';'),
    [articoli, sel, qta]
  );

  // Dirty quando ci sono righe selezionate e il wizard è in corso
  const isDirty = risultati === null && nRigheSelezionate > 0;
  useDirtyForm(isDirty, 'Ordini a fornitore — composizione');

  // ── Composizione (step 2) ───────────────────────────────────────────────────

  // Proposta iniziale: un ordine per fornitore abituale, gli articoli senza
  // fornitore finiscono in una card "Fornitore da assegnare" (in fondo).
  const buildComposizione = (): OrdineProposto[] => {
    const perFornitore = new Map<string, OrdineProposto>();
    const sedeDefault = sedi.length > 0 ? String(sedi[0].id) : '';
    for (const a of articoli) {
      const s = sel.get(a.chiave);
      if (!s || s.size === 0) continue;
      for (const sorg of a.sorgenti) {
        if (!s.has(sorg.rigaRdaId)) continue;
        const qNum = parseItalianNumber(qta.get(sorg.rigaRdaId) ?? '');
        if (!Number.isFinite(qNum) || qNum <= 0) continue;
        const key = a.fornitoreAbituale ? `F:${a.fornitoreAbituale.id}` : 'SENZA';
        if (!perFornitore.has(key)) {
          perFornitore.set(key, {
            uid: generaUid(),
            fornitoreId: a.fornitoreAbituale?.id ?? null,
            sedeId: sedeDefault,
            note: '',
            righe: [],
          });
        }
        perFornitore.get(key)!.righe.push({
          rigaRdaId: sorg.rigaRdaId,
          chiave: a.chiave,
          articoloId: a.articoloId,
          codice: a.codice,
          descrizione: a.descrizione,
          unitaMisura: a.unitaMisura,
          quantita: qNum,
          prezzo: a.prezzoStandard != null ? formatNumberInput(a.prezzoStandard, false) : '',
          rdaEtichetta: sorg.rdaEtichetta,
        });
      }
    }
    // Card "Fornitore da assegnare" sempre in fondo
    return Array.from(perFornitore.entries())
      .sort((x, y) => (x[0] === 'SENZA' ? 1 : 0) - (y[0] === 'SENZA' ? 1 : 0))
      .map(e => e[1]);
  };

  const avantiComposizione = () => {
    if (nRigheSelezionate === 0) return;
    // Valida le quantità delle sorgenti selezionate: 0 < q <= residuo
    const errori = new Set<number>();
    const daEspandere = new Set<string>();
    for (const a of articoli) {
      const s = sel.get(a.chiave);
      if (!s || s.size === 0) continue;
      for (const sorg of a.sorgenti) {
        if (!s.has(sorg.rigaRdaId)) continue;
        const qNum = parseItalianNumber(qta.get(sorg.rigaRdaId) ?? '');
        if (!Number.isFinite(qNum) || qNum <= 0 || qNum > sorg.residuo + 1e-9) {
          errori.add(sorg.rigaRdaId);
          daEspandere.add(a.chiave);
        }
      }
    }
    if (errori.size > 0) {
      setErroriQta(errori);
      setEspansi(prev => new Set([...Array.from(prev), ...Array.from(daEspandere)]));
      showToast(
        'Controlla le quantità evidenziate: devono essere maggiori di zero e non superiori al residuo',
        'error'
      );
      return;
    }
    setErroriQta(new Set());
    // Rigenera solo se selezione/quantità sono cambiate rispetto alla composizione corrente
    if (firmaSelezione !== firmaComposizione) {
      setOrdini(buildComposizione());
      setFirmaComposizione(firmaSelezione);
    }
    setStep(2);
  };

  const numeroByUid = useMemo(() => {
    const m = new Map<number, number>();
    ordini.forEach((o, i) => m.set(o.uid, i + 1));
    return m;
  }, [ordini]);

  const setFornitoreCard = (uid: number, id: string | null) => {
    setOrdini(prev => prev.map(o =>
      o.uid === uid ? { ...o, fornitoreId: id ? parseInt(id, 10) : null } : o
    ));
  };

  const setSedeCard = (uid: number, sedeId: string) => {
    setOrdini(prev => prev.map(o => (o.uid === uid ? { ...o, sedeId } : o)));
  };

  const setNoteCard = (uid: number, note: string) => {
    setOrdini(prev => prev.map(o => (o.uid === uid ? { ...o, note } : o)));
  };

  const setPrezzoRiga = (uid: number, rigaRdaId: number, val: string) => {
    setOrdini(prev => prev.map(o =>
      o.uid === uid
        ? { ...o, righe: o.righe.map(r => (r.rigaRdaId === rigaRdaId ? { ...r, prezzo: val } : r)) }
        : o
    ));
  };

  // Esplode la card in una card per ogni articolo distinto
  const dividiPerArticolo = (uid: number) => {
    const idx = ordini.findIndex(o => o.uid === uid);
    if (idx < 0) return;
    const o = ordini[idx];
    const perArticolo = new Map<string, RigaProposta[]>();
    for (const r of o.righe) {
      if (!perArticolo.has(r.chiave)) perArticolo.set(r.chiave, []);
      perArticolo.get(r.chiave)!.push(r);
    }
    if (perArticolo.size <= 1) return;
    const esplose: OrdineProposto[] = Array.from(perArticolo.values()).map(righe => ({
      uid: generaUid(),
      fornitoreId: o.fornitoreId,
      sedeId: o.sedeId,
      note: o.note,
      righe,
    }));
    setOrdini([...ordini.slice(0, idx), ...esplose, ...ordini.slice(idx + 1)]);
  };

  // Sposta una riga in un'altra card (o in un nuovo ordine)
  const spostaRiga = (fromUid: number, riga: RigaProposta, target: string) => {
    const from = ordini.find(o => o.uid === fromUid);
    if (!from) return;
    let next = ordini.map(o =>
      o.uid === fromUid
        ? { ...o, righe: o.righe.filter(r => r.rigaRdaId !== riga.rigaRdaId) }
        : o
    );
    if (target === 'nuovo') {
      next = [...next, {
        uid: generaUid(),
        fornitoreId: from.fornitoreId,
        sedeId: from.sedeId,
        note: '',
        righe: [riga],
      }];
    } else {
      const targetUid = parseInt(target, 10);
      next = next.map(o =>
        o.uid === targetUid ? { ...o, righe: [...o.righe, riga] } : o
      );
    }
    // Una card svuotata sparisce
    setOrdini(next.filter(o => o.righe.length > 0));
  };

  const nRigheComposizione = useMemo(
    () => ordini.reduce((tot, o) => tot + o.righe.length, 0),
    [ordini]
  );

  // Card incomplete (senza fornitore o senza sede) → bloccano la creazione
  const problemiCard = useMemo(() => {
    const problemi: string[] = [];
    ordini.forEach((o, i) => {
      const mancanze: string[] = [];
      if (!o.fornitoreId) mancanze.push('fornitore da assegnare');
      if (!o.sedeId) mancanze.push('sede da selezionare');
      if (mancanze.length > 0) problemi.push(`Ordine ${i + 1}: ${mancanze.join(' e ')}`);
    });
    return problemi;
  }, [ordini]);

  // ── Generazione ─────────────────────────────────────────────────────────────

  const crea = async () => {
    if (ordini.length === 0 || creando || problemiCard.length > 0) return;
    const payload = {
      societaId: parseInt(societaIdEff, 10),
      ordini: ordini.map(o => ({
        fornitoreId: o.fornitoreId,
        sedeId: parseInt(o.sedeId, 10),
        note: o.note.trim() || undefined,
        righe: o.righe.map(r => {
          const p = parseItalianNumber(r.prezzo);
          return {
            rigaRdaId: r.rigaRdaId,
            articoloId: r.articoloId ?? undefined,
            descrizione: r.descrizione,
            unitaMisura: r.unitaMisura ?? undefined,
            quantita: r.quantita,
            prezzoUnitarioAtteso: Number.isFinite(p) ? p : undefined,
          };
        }),
      })),
    };

    setCreando(true);
    try {
      // @api POST /api/ordini-fornitori/genera-composti {"societaId":"number","ordini":[{"fornitoreId":"number","sedeId":"number","note":"string","righe":[{"rigaRdaId":"number","articoloId":"number","descrizione":"string","unitaMisura":"string","quantita":"number","prezzoUnitarioAtteso":"number"}]}]} -> {"success":true,"data":{"risultati":[{"esito":"CREATO|ERRORE","ordineFornitoreId":"number","fornitoreRagioneSociale":"string","nRighe":"number","motivo":"string"}],"creati":"number"}}
      const res = await apiFetch<{
        success: boolean;
        data?: { risultati: RisultatoOrdine[]; creati: number };
        error?: unknown;
      }>('/api/ordini-fornitori/genera-composti', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!res.success || !res.data) {
        showToast(estraiErrore({ payload: res }, 'Errore nella creazione degli ordini'), 'error');
        return;
      }
      const creati = res.data.creati || 0;
      showToast(`${creati} ordin${creati === 1 ? 'e creato' : 'i creati'} in bozza`, 'success');
      setRisultati(res.data.risultati || []);
      setCreatiCount(creati);
    } catch (err: unknown) {
      showToast(estraiErrore(err, 'Errore nella creazione degli ordini'), 'error');
    } finally {
      setCreando(false);
    }
  };

  // Reset completo e ricarica degli articoli residui
  const componiAltri = () => {
    setRisultati(null);
    setCreatiCount(0);
    setStep(1);
    setOrdini([]);
    setFirmaComposizione('');
    caricaArticoli();
  };

  // ── Render: sottotitolo dinamico ────────────────────────────────────────────

  const sottotitolo = risultati !== null
    ? 'Esito della generazione degli ordini in bozza'
    : step === 1
      ? 'Seleziona gli articoli da ordinare: le quantità arrivano dalle RDA aperte'
      : 'Componi gli ordini: assegna fornitore e sede, sposta le righe tra le card prima di creare le bozze';

  return (
    <Layout>
      <div className="wof">
        {commessaIdFiltro ? (
          <BopBackLink to={`/commerciale/commesse/${commessaIdFiltro}`} label="Torna alla commessa" />
        ) : (
          <BopBackLink to="/commerciale/passivo/ordini" label="Torna agli ordini fornitori" />
        )}

        {/* Header */}
        <div className="bop-page-header">
          <div className="wof-header-titles">
            <h1 className="bop-page-title">Ordini a fornitore</h1>
            <p className="wof-nota bop-text-muted">{sottotitolo}</p>
          </div>
          {risultati === null && (
            <div className="wof-steps">
              <span className={`wof-step ${step === 1 ? 'wof-step--active' : ''}`}>
                <span className="wof-step__num">1</span> Da ordinare
              </span>
              <span className="wof-step__sep" aria-hidden="true">→</span>
              <span className={`wof-step ${step === 2 ? 'wof-step--active' : ''}`}>
                <span className="wof-step__num">2</span> Composizione
              </span>
            </div>
          )}
        </div>

        {commessaIdFiltro && risultati === null && (
          <div className="wof-filtro-commessa">
            <span className="wof-filtro-commessa__testo">
              Solo le righe RDA della commessa{' '}
              <strong>
                {commessaFiltrata
                  ? `${commessaFiltrata.numero}${commessaFiltrata.descrizione ? ` — ${commessaFiltrata.descrizione}` : ''}`
                  : `#${commessaIdFiltro}`}
              </strong>
            </span>
            <button className="bop-btn bop-btn-ghost bop-btn-sm" onClick={rimuoviFiltroCommessa}>
              Mostra tutte le RDA
            </button>
          </div>
        )}

        {risultati !== null ? (
          /* ── Pannello esiti ─────────────────────────────────────────────── */
          <div className="bop-card">
            <div className="bop-card__header">
              <h2 className="bop-card__title">
                Esito generazione — {creatiCount} ordin{creatiCount === 1 ? 'e creato' : 'i creati'}
              </h2>
            </div>
            <div className="bop-card__body">
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Fornitore</th>
                    <th>Esito</th>
                    <th>Bozza</th>
                    <th>Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {risultati.length === 0 ? (
                    <tr>
                      <td colSpan={4}>
                        <div className="bop-empty-state">Nessun esito da mostrare</div>
                      </td>
                    </tr>
                  ) : (
                    risultati.map((r, i) => (
                      <tr key={`${r.ordineFornitoreId ?? 'x'}-${i}`}>
                        <td>{r.fornitoreRagioneSociale || <span className="bop-text-muted">—</span>}</td>
                        <td>{renderEsito(r.esito)}</td>
                        <td>
                          {r.ordineFornitoreId ? (
                            <Link
                              className="wof-ordine-link"
                              to={`/commerciale/passivo/ordini/${r.ordineFornitoreId}`}
                            >
                              Apri bozza ({r.nRighe ?? 0} righ{(r.nRighe ?? 0) === 1 ? 'a' : 'e'})
                            </Link>
                          ) : (
                            <span className="bop-text-muted">—</span>
                          )}
                        </td>
                        <td>
                          {r.motivo
                            ? <span className="bop-text-muted">{r.motivo}</span>
                            : <span className="bop-text-muted">—</span>}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <div className="wof-esiti-actions">
                <button className="bop-btn bop-btn-secondary" onClick={componiAltri}>
                  Componi altri
                </button>
                {commessaIdFiltro ? (
                  <button
                    className="bop-btn bop-btn-primary"
                    onClick={() => navigate(`/commerciale/commesse/${commessaIdFiltro}`)}
                  >
                    Torna alla commessa
                  </button>
                ) : (
                  <button
                    className="bop-btn bop-btn-primary"
                    onClick={() => navigate('/commerciale/passivo/ordini')}
                  >
                    Vai agli ordini
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : step === 1 ? (
          /* ── STEP 1 — Da ordinare ───────────────────────────────────────── */
          <>
            {/* Filtri */}
            <div className="bop-filter-bar wof-filters-row">
              {!isDisabled && (
                <div className="bop-form-group">
                  <label className="bop-label">Società</label>
                  <select
                    className="bop-select"
                    value={societaIdLocale}
                    onChange={e => setSocietaIdLocale(e.target.value)}
                  >
                    {isSuperadmin && <option value="">Seleziona società…</option>}
                    {listaSocieta.map(s => (
                      <option key={s.id} value={String(s.id)}>{s.nome}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="bop-form-group">
                <label className="bop-label">Cerca</label>
                <input
                  className="bop-input wof-search-input"
                  value={qInput}
                  onChange={e => setQInput(e.target.value)}
                  placeholder="Codice, descrizione, fornitore, RDA, ordine, commessa..."
                />
              </div>
            </div>

            {/* Tabella articoli raggruppati */}
            {!pronta || !societaIdEff ? (
              <div className="bop-card">
                <div className="bop-empty-state">
                  Seleziona una società per visualizzare gli articoli da ordinare
                </div>
              </div>
            ) : loading ? (
              <div className="bop-card">
                <div className="bop-empty-state">Caricamento…</div>
              </div>
            ) : articoliFiltrati.length === 0 ? (
              <div className="bop-card">
                <div className="bop-empty-state">
                  <h3 className="bop-empty-state__title">Nessun articolo da ordinare</h3>
                  <p className="bop-empty-state__text">
                    Non ci sono righe di RDA aperte con quantità residue da ordinare.
                  </p>
                </div>
              </div>
            ) : (
              <div className="bop-card">
                <table className="bop-table wof-tabella-articoli">
                  <thead>
                    <tr>
                      <th className="wof-col-check"></th>
                      <th className="wof-col-chevron"></th>
                      <th>Codice</th>
                      <th>Descrizione</th>
                      <th>Fornitore abituale</th>
                      <th>U.M.</th>
                      <th className="bop-text-right">Da ordinare</th>
                      <th className="bop-text-right">Prezzo std</th>
                    </tr>
                  </thead>
                  <tbody>
                    {articoliFiltrati.map(a => {
                      const selArticolo = sel.get(a.chiave) || new Set<number>();
                      const tuttiSel = a.sorgenti.length > 0 && selArticolo.size === a.sorgenti.length;
                      const parziale = selArticolo.size > 0 && !tuttiSel;
                      const aperto = espansi.has(a.chiave);
                      return (
                        <React.Fragment key={a.chiave}>
                          <tr
                            className="wof-row-articolo"
                            onClick={() => toggleEspanso(a.chiave)}
                          >
                            <td className="wof-col-check">
                              <input
                                type="checkbox"
                                checked={tuttiSel}
                                ref={el => { if (el) el.indeterminate = parziale; }}
                                onChange={() => toggleArticolo(a)}
                                onClick={e => e.stopPropagation()}
                              />
                            </td>
                            <td className="wof-col-chevron">
                              <span className="wof-chevron" aria-hidden="true">
                                {aperto ? '▾' : '▸'}
                              </span>
                            </td>
                            <td>
                              {a.codice
                                ? <span className="wof-codice">{a.codice}</span>
                                : <span className="bop-text-muted">—</span>}
                            </td>
                            <td>{a.descrizione}</td>
                            <td>
                              {a.fornitoreAbituale
                                ? a.fornitoreAbituale.ragioneSociale
                                : <span className="bop-badge bop-badge-warning">Da assegnare</span>}
                            </td>
                            <td>{a.unitaMisura || <span className="bop-text-muted">—</span>}</td>
                            <td className="bop-text-right">{formatQuantity(a.totaleDaOrdinare)}</td>
                            <td className="bop-text-right">
                              {a.prezzoStandard != null
                                ? formatCurrency(a.prezzoStandard)
                                : <span className="bop-text-muted">—</span>}
                            </td>
                          </tr>
                          {aperto && (
                            <tr className="wof-row-sorgenti">
                              <td colSpan={8} className="wof-sorgenti-cell">
                                <div className="wof-sorgenti">
                                  <table className="bop-table wof-sorgenti-table">
                                    <thead>
                                      <tr>
                                        <th className="wof-col-check"></th>
                                        <th>RDA</th>
                                        <th>Ordine</th>
                                        <th>Commessa</th>
                                        <th className="bop-text-right">Q.tà</th>
                                        <th className="bop-text-right">Ordinata</th>
                                        <th className="bop-text-right">Residuo</th>
                                        <th className="bop-text-right">Q.tà da ordinare</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {a.sorgenti.map(s => (
                                        <tr key={s.rigaRdaId}>
                                          <td className="wof-col-check">
                                            <input
                                              type="checkbox"
                                              checked={selArticolo.has(s.rigaRdaId)}
                                              onChange={() => toggleSorgente(a.chiave, s.rigaRdaId)}
                                            />
                                          </td>
                                          <td>
                                            <span className="wof-rda-etichetta">{s.rdaEtichetta}</span>
                                            {s.note && (
                                              <span className="wof-nota-sorgente bop-text-muted">{s.note}</span>
                                            )}
                                          </td>
                                          <td>
                                            {s.ordine
                                              ? s.ordine.numero
                                              : <span className="bop-text-muted">—</span>}
                                          </td>
                                          <td>
                                            {s.commessa ? (
                                              <span>
                                                {s.commessa.numero}
                                                {s.commessa.descrizione ? ` — ${s.commessa.descrizione}` : ''}
                                              </span>
                                            ) : (
                                              <span className="bop-text-muted">—</span>
                                            )}
                                          </td>
                                          <td className="bop-text-right">{formatQuantity(s.quantita)}</td>
                                          <td className="bop-text-right">{formatQuantity(s.ordinata)}</td>
                                          <td className="bop-text-right">{formatQuantity(s.residuo)}</td>
                                          <td className="bop-text-right">
                                            <input
                                              className={`bop-input wof-qta-input ${erroriQta.has(s.rigaRdaId) ? 'wof-qta-input--error' : ''}`}
                                              value={qta.get(s.rigaRdaId) ?? ''}
                                              onChange={e => setQtaSorgente(s.rigaRdaId, e.target.value)}
                                              disabled={!selArticolo.has(s.rigaRdaId)}
                                              inputMode="decimal"
                                            />
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Footer sticky step 1 */}
            <div className="bop-card wof-footer">
              <div className="bop-card__body">
                <div className="wof-footer-row">
                  <span className="wof-riepilogo-testo">
                    <strong className="wof-riepilogo-count">{nRigheSelezionate}</strong>{' '}
                    righ{nRigheSelezionate === 1 ? 'a selezionata' : 'e selezionate'}
                  </span>
                  <div className="wof-footer-actions">
                    <button
                      className="bop-btn bop-btn-primary"
                      onClick={avantiComposizione}
                      disabled={nRigheSelezionate === 0}
                    >
                      Avanti: componi gli ordini →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          /* ── STEP 2 — Composizione ──────────────────────────────────────── */
          <>
            {ordini.length === 0 ? (
              <div className="bop-card">
                <div className="bop-empty-state">
                  <h3 className="bop-empty-state__title">Nessun ordine da comporre</h3>
                  <p className="bop-empty-state__text">
                    Torna agli articoli e seleziona almeno una riga da ordinare.
                  </p>
                </div>
              </div>
            ) : (
              ordini.map((o, idx) => {
                const numero = idx + 1;
                const fornNome = nomeFornitore(o.fornitoreId);
                const { totale, completo } = totaleCard(o);
                const articoliDistinti = new Set(o.righe.map(r => r.chiave)).size;
                const altre = ordini.filter(x => x.uid !== o.uid);
                const puoNuovo = o.righe.length > 1;
                return (
                  <div className="bop-card wof-ordine-card" key={o.uid}>
                    <div className="wof-ordine-card__header">
                      <div className="wof-ordine-card__titolo-blocco">
                        <span className="wof-ordine-card__titolo">
                          Ordine {numero} — {fornNome || 'Fornitore da assegnare'}
                        </span>
                        <span className="wof-ordine-card__totale">
                          Totale stimato:{' '}
                          <span className="wof-ordine-card__totale-importo">
                            {completo ? formatCurrency(totale) : '—'}
                          </span>
                          <span className="bop-text-muted">
                            {' '}— {o.righe.length} righ{o.righe.length === 1 ? 'a' : 'e'}
                          </span>
                        </span>
                      </div>
                      {articoliDistinti > 1 && (
                        <button
                          className="bop-btn bop-btn-secondary wof-ordine-card__dividi"
                          onClick={() => dividiPerArticolo(o.uid)}
                        >
                          Dividi: un ordine per articolo
                        </button>
                      )}
                    </div>
                    <div className="wof-ordine-card__config">
                      <div className="bop-form-group wof-config-fornitore">
                        <label className="bop-label">Fornitore</label>
                        <ComboFiltro
                          label="Fornitore"
                          value={o.fornitoreId != null ? String(o.fornitoreId) : null}
                          onChange={id => setFornitoreCard(o.uid, id)}
                          items={fornitoriItems}
                        />
                      </div>
                      <div className="bop-form-group wof-config-sede">
                        <label className="bop-label">Sede</label>
                        <select
                          className="bop-select"
                          value={o.sedeId}
                          onChange={e => setSedeCard(o.uid, e.target.value)}
                        >
                          {!o.sedeId && <option value="">Seleziona sede…</option>}
                          {sedi.map(s => (
                            <option key={s.id} value={String(s.id)}>{s.nome}</option>
                          ))}
                        </select>
                        <span className="wof-config-hint bop-text-muted">
                          La sede determina la numerazione
                        </span>
                      </div>
                      <div className="bop-form-group wof-config-note">
                        <label className="bop-label">Note</label>
                        <textarea
                          className="bop-input wof-note-textarea"
                          value={o.note}
                          onChange={e => setNoteCard(o.uid, e.target.value)}
                          placeholder="Note per l'ordine (opzionali)"
                          rows={2}
                        />
                      </div>
                    </div>
                    <div className="wof-ordine-card__body">
                      <table className="bop-table">
                        <thead>
                          <tr>
                            <th>Codice</th>
                            <th>Descrizione</th>
                            <th>RDA di origine</th>
                            <th className="bop-text-right">Q.tà</th>
                            <th className="bop-text-right">Prezzo atteso</th>
                            <th className="wof-col-sposta">Sposta in…</th>
                          </tr>
                        </thead>
                        <tbody>
                          {o.righe.map(r => (
                            <tr key={r.rigaRdaId}>
                              <td>
                                {r.codice
                                  ? <span className="wof-codice">{r.codice}</span>
                                  : <span className="bop-text-muted">—</span>}
                              </td>
                              <td>{r.descrizione}</td>
                              <td><span className="wof-rda-etichetta">{r.rdaEtichetta}</span></td>
                              <td className="bop-text-right">
                                {formatQuantity(r.quantita)}
                                {r.unitaMisura && (
                                  <span className="bop-text-muted"> {r.unitaMisura}</span>
                                )}
                              </td>
                              <td className="bop-text-right">
                                <input
                                  className="bop-input wof-prezzo-input"
                                  value={r.prezzo}
                                  onChange={e => setPrezzoRiga(o.uid, r.rigaRdaId, e.target.value)}
                                  placeholder="—"
                                  inputMode="decimal"
                                />
                              </td>
                              <td className="wof-col-sposta">
                                {(altre.length > 0 || puoNuovo) ? (
                                  <select
                                    className="bop-select wof-sposta-select"
                                    value=""
                                    onChange={e => {
                                      if (e.target.value) spostaRiga(o.uid, r, e.target.value);
                                    }}
                                  >
                                    <option value="">Sposta in…</option>
                                    {altre.map(x => (
                                      <option key={x.uid} value={String(x.uid)}>
                                        Ordine {numeroByUid.get(x.uid)} — {nomeFornitore(x.fornitoreId) || 'da assegnare'}
                                      </option>
                                    ))}
                                    {puoNuovo && <option value="nuovo">Nuovo ordine</option>}
                                  </select>
                                ) : (
                                  <span className="bop-text-muted">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })
            )}

            {/* Footer sticky step 2 */}
            <div className="bop-card wof-footer">
              <div className="bop-card__body">
                <div className="wof-footer-row">
                  <span className="wof-riepilogo-testo">
                    {ordini.length === 1 ? (
                      <>Verrà creato <strong className="wof-riepilogo-count">1</strong> ordine in bozza</>
                    ) : (
                      <>
                        Verranno creati{' '}
                        <strong className="wof-riepilogo-count">{ordini.length}</strong> ordini in bozza
                      </>
                    )}
                    {nRigheComposizione > 0 && (
                      <span className="bop-text-muted">
                        {' '}— {nRigheComposizione} righ{nRigheComposizione === 1 ? 'a' : 'e'}
                      </span>
                    )}
                  </span>
                  <div className="wof-footer-actions">
                    <button
                      className="bop-btn bop-btn-secondary"
                      onClick={() => setStep(1)}
                      disabled={creando}
                    >
                      ← Torna agli articoli
                    </button>
                    <button
                      className="bop-btn bop-btn-primary"
                      onClick={crea}
                      disabled={creando || ordini.length === 0 || problemiCard.length > 0}
                    >
                      {creando
                        ? 'Creazione…'
                        : `Crea ${ordini.length} ordin${ordini.length === 1 ? 'e' : 'i'} in bozza`}
                    </button>
                  </div>
                </div>
                {problemiCard.length > 0 && (
                  <p className="wof-footer-problemi bop-text-danger">
                    {problemiCard.join('; ')}
                  </p>
                )}
              </div>
            </div>
          </>
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
